// client/src/pages/Admin/ResourceManager/components/ResourceSearch.tsx

import React, { useRef, useState, useEffect } from 'react';
import { Search, X, Tag, Users, FolderTree, File } from 'lucide-react';
import { highlightMatch } from '../utils/resourceSearch';

interface ResourceSearchProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onClearSearch: () => void;
  isFocused: boolean;
  onFocus: () => void;
  onBlur: () => void;
  suggestions?: string[];
  onSuggestionClick?: (suggestion: string) => void;
}

export const ResourceSearch: React.FC<ResourceSearchProps> = ({
  searchTerm,
  onSearchChange,
  onClearSearch,
  isFocused,
  onFocus,
  onBlur,
  suggestions = [],
  onSuggestionClick
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    setShowSuggestions(suggestions.length > 0 && searchTerm.length >= 2);
  }, [suggestions, searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setShowSuggestions(false);
      return;
    }

    if (e.key === 'Enter' && showSuggestions && suggestions.length > 0 && onSuggestionClick) {
      onSuggestionClick(suggestions[0]);
      e.preventDefault();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    if (onSuggestionClick) {
      onSuggestionClick(suggestion);
    }
    setShowSuggestions(false);
  };

  return (
    <div className={`search-box ${isFocused ? 'focused' : ''}`}>
      <Search size={18} className="search-icon" />
      <input
        ref={inputRef}
        id="search"
        name="search"
        type="text"
        placeholder="Search resources..."
        value={searchTerm}
        onChange={(e) => onSearchChange(e.target.value)}
        onFocus={() => {
          onFocus();
          if (searchTerm.length >= 2 && suggestions.length > 0) {
            setShowSuggestions(true);
          }
        }}
        onBlur={() => {
          setTimeout(() => {
            onBlur();
          }, 200);
        }}
        onKeyDown={handleKeyDown}
        aria-label="Search resources"
        aria-autocomplete="list"
        aria-expanded={showSuggestions}
        role="combobox"
      />
      {searchTerm && (
        <button
          type="button"
          className="clear-search"
          onClick={onClearSearch}
          aria-label="Clear search"
        >
          <X size={16} />
        </button>
      )}

      {showSuggestions && suggestions.length > 0 && (
        <div 
          ref={suggestionsRef}
          className="search-suggestions"
          role="listbox"
          aria-label="Search suggestions"
        >
          <div className="suggestions-header">
            <span className="suggestions-label">Suggestions</span>
            <span className="suggestions-count">{suggestions.length}</span>
          </div>
          {suggestions.map((suggestion, index) => {
            const parts = suggestion.split(': ');
            const isTag = parts[0] === 'Tag';
            const isAuthor = parts[0] === 'Author';
            const isCategory = parts[0] === 'Category';
            
            return (
              <button
                key={index}
                type="button"
                className="suggestion-item"
                onClick={() => handleSuggestionClick(suggestion)}
                role="option"
                aria-selected={false}
              >
                {isTag && <Tag size={14} className="suggestion-icon tag-icon" />}
                {isAuthor && <Users size={14} className="suggestion-icon author-icon" />}
                {isCategory && <FolderTree size={14} className="suggestion-icon category-icon" />}
                {!isTag && !isAuthor && !isCategory && <File size={14} className="suggestion-icon" />}
                <span 
                  className="suggestion-text"
                  dangerouslySetInnerHTML={{ 
                    __html: highlightMatch(suggestion, searchTerm) 
                  }}
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};